/**
 * Type-safe deep property access. Replaces lodash/get for bundle size.
 * Supports dot notation paths (e.g. 'a.b.c') and array paths (e.g. ['a', 'b', 'c']).
 *
 * When using a literal path on a typed object, provides:
 * - Path autocomplete (valid keys at each level)
 * - Inferred return type
 */

/** Extracts the value type at path P in object T. Supports dot notation. */
export type PathValue<T, P extends string> = string extends P
  ? unknown
  : P extends `${infer K}.${infer R}`
    ? K extends keyof T
      ? PathValue<NonNullable<T[K]>, R>
      : undefined
    : P extends keyof T
      ? T[P]
      : undefined;

/** Valid dot-separated paths for object T (for autocomplete). Limited depth to avoid "excessively deep" errors. */
type PathImpl<T, Depth extends readonly number[]> = T extends object
  ? Depth['length'] extends 5
    ? keyof T & string
    : {
          [K in keyof T]: K extends string
            ? T[K] extends Record<string, unknown>
              ? K | `${K}.${PathImpl<T[K], [...Depth, 0]>}`
              : K
            : never;
        }[keyof T] extends infer P
      ? P extends string
        ? P
        : never
      : never
  : never;

export type Path<T> = PathImpl<T, []>;

function getImpl(obj: unknown, path: string | (string | number)[]): unknown {
  if (obj == null) return undefined;
  const keys = Array.isArray(path) ? path : path.split('.');
  let result: unknown = obj;
  for (const key of keys) {
    if (result == null) return undefined;
    result = (result as Record<string, unknown>)[String(key)];
  }
  return result;
}

/**
 * Safe deep property access with optional type inference.
 * Use literal paths (e.g. get(obj, 'a.b.c')) for return type inference.
 * For path autocomplete, type the path as Path<T>: const path: Path<typeof obj> = 'a.b'
 */
export function get<T, P extends string>(obj: T, path: P): PathValue<T, P> | undefined {
  return getImpl(obj, path) as PathValue<T, P> | undefined;
}
